import React, { useState, useEffect } from 'react';
import { X, Download, Link2, Check, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import { useGenerateQRCodeQuery } from '../../store/services/api';

interface QRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  shareUrl: string;
  eventUrl: string;
}

const QRCodeModal = ({ isOpen, onClose, title, shareUrl, eventUrl }: QRCodeModalProps) => {
  const [copied, setCopied] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);

  const { 
    data: qrCodeBlob, 
    isLoading, 
    error, 
    refetch 
  } = useGenerateQRCodeQuery(eventUrl, { skip: !isOpen });

  // QR code blob'unu URL'e çevir
  useEffect(() => {
    if (qrCodeBlob) {
      const imageUrl = URL.createObjectURL(qrCodeBlob);
      setQrCodeUrl(imageUrl);
      
      // Cleanup
      return () => {
        URL.revokeObjectURL(imageUrl);
      };
    }
  }, [qrCodeBlob]);

  if (!isOpen) return null;

  const handleDownload = () => {
    if (!qrCodeUrl) return;
    
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `${title.toLowerCase().replace(/\s+/g, '-')}-qr-code.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg w-full max-w-sm">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#CCFF00]/10 rounded-md flex items-center justify-center">
              {qrCodeUrl && !isLoading ? (
                <img src={qrCodeUrl} alt="QR Icon" className="w-5 h-5" />
              ) : (
                <div className="w-5 h-5 flex items-center justify-center">
                  <Loader2 className="w-4 h-4 animate-spin text-[#FF4444]" />
                </div>
              )}
            </div>
            <h2 className="text-base font-medium">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* QR Code */}
        <div className="p-4">
          <div className="bg-gray-50 p-4 rounded-md flex items-center justify-center mb-4 h-40">
            {isLoading ? (
              <Loader2 className="w-10 h-10 animate-spin text-[#FF4444]" />
            ) : error ? (
              <div className="text-center">
                <p className="text-red-500 text-sm">Failed to load QR code</p>
                <button 
                  className="text-[#FF4444] text-xs mt-2 underline"
                  onClick={() => refetch()}
                >
                  Retry
                </button>
              </div>
            ) : qrCodeUrl ? (
              <img src={qrCodeUrl} alt="QR Code" className="w-40 h-40" />
            ) : null}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleDownload}
              className="flex-1 h-8 text-xs flex items-center justify-center gap-1"
              disabled={!qrCodeUrl || isLoading}
            >
              <Download className="w-3.5 h-3.5" />
              Download
            </Button>
            <Button
              onClick={handleCopyUrl}
              className={`flex-1 h-8 text-xs flex items-center justify-center gap-1 transition-all ${
                copied ? 'bg-[#CCFF00] text-black' : ''
              }`}
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  Copied!
                </>
              ) : (
                <>
                  <Link2 className="w-3.5 h-3.5" />
                  Copy URL
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeModal;